import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

/**
 * AI feature types that have progress messages
 */
export enum AIFeatureType {
  MAGIC_ERASER = 'magic_eraser',
  VIRTUAL_STAGING = 'virtual_staging',
  DECLUTTER_ROOM = 'declutter_room',
  PAINT_WALLS = 'paint_walls',
  FLOOR_REPLACEMENT = 'floor_replacement',
  ENHANCEMENT = 'enhancement',
}

/**
 * Processing steps that can be reported by the AI processors
 */
export enum AIProcessingStep {
  STARTING = 'starting',
  UPLOADING_MASK = 'uploading_mask',
  PROCESSING = 'processing',
  PROCESSING_EXTENDED = 'processing_extended', // For longer operations
  FETCHING_RESULT = 'fetching_result',
  FINALIZING = 'finalizing',
}

/** Starting phase: 4-5s, Processing phase: 5-8s */
const getDelayForIndex = (index: number) => {
  if (index < 2) return 4000 + Math.random() * 1000
  return 5000 + Math.random() * 3000
}

interface UseAIProgressMessagesProps {
  /** The AI feature type being processed */
  featureType: AIFeatureType
  /** Whether the AI processing is currently active */
  isProcessing: boolean
  /** Current processing step (optional - kept for API compatibility) */
  currentStep?: AIProcessingStep
  /** Whether to auto-rotate messages (default: true) */
  autoRotate?: boolean
}

interface UseAIProgressMessagesReturn {
  /** Current message to display */
  currentMessage: string
  /** Current message index */
  currentMessageIndex: number
  /** Total number of messages */
  totalMessages: number
  /** Manually advance to next message */
  nextMessage: () => void
  /** Reset to first message */
  reset: () => void
  /** Set a specific processing step */
  setStep: (step: AIProcessingStep) => void
}

/**
 * Hook that provides dynamic, user-friendly progress messages during AI operations.
 *
 * Messages progress linearly through all phases (starting → processing → finalizing)
 * on a timer with varying intervals (4-5s), creating a natural sense of progression.
 * When reaching the last message, it stays there until processing completes.
 *
 * @example
 * ```tsx
 * const { currentMessage } = useAIProgressMessages({
 *   featureType: AIFeatureType.MAGIC_ERASER,
 *   isProcessing: true,
 * })
 *
 * return <LoadingOverlay message={currentMessage} />
 * ```
 */
export const useAIProgressMessages = ({
  featureType,
  isProcessing,
  autoRotate = true,
}: UseAIProgressMessagesProps): UseAIProgressMessagesReturn => {
  const { t } = useTranslation(['photo_studio_editing'])
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /**
   * Build a flat list of ALL messages in order across all phases.
   * This creates a natural time-based progression:
   * starting → processing → finalizing
   *
   * The last message stays visible until processing completes.
   */
  const allMessages = useMemo((): string[] => {
    const baseKey = `ai_progress.${featureType}`
    return [
      t(`${baseKey}.starting.preparing`),
      t(`${baseKey}.starting.initializing`),
      t(`${baseKey}.processing.step_1`),
      t(`${baseKey}.processing.step_2`),
      t(`${baseKey}.finalizing.wrapping_up`),
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureType])

  const totalMessages = allMessages.length

  // Get current message with bounds checking
  const currentMessage = allMessages[Math.min(currentMessageIndex, totalMessages - 1)] || allMessages[0]

  // Advance to next message (stops at last)
  const nextMessage = useCallback(() => {
    setCurrentMessageIndex(prev => Math.min(prev + 1, totalMessages - 1))
  }, [totalMessages])

  // Reset to first message
  const reset = useCallback(() => {
    setCurrentMessageIndex(0)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Set processing step (kept for API compatibility, no-op in auto-rotate mode)
  const setStep = useCallback((_step: AIProcessingStep) => {
    // In flat progression mode, steps are managed by the timer
  }, [])

  // Auto-rotate: starting 4-5s, processing 5-8s, finalizing stays till end
  useEffect(() => {
    if (!isProcessing || !autoRotate) {
      if (!isProcessing) reset()
      return
    }

    const scheduleNext = (index: number) => {
      if (index >= totalMessages - 1) return // Stay on last message

      const delay = getDelayForIndex(index)
      timeoutRef.current = setTimeout(() => {
        setCurrentMessageIndex(index + 1)
        scheduleNext(index + 1)
      }, delay)
    }

    scheduleNext(currentMessageIndex)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isProcessing, autoRotate, totalMessages, reset, currentMessageIndex])

  return {
    currentMessage,
    currentMessageIndex,
    totalMessages,
    nextMessage,
    reset,
    setStep,
  }
}
